// @ts-check
/// <reference path="../../types/index.js" />

import React, { useContext } from 'react';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import DialogActions from '@mui/material/DialogActions';
import FormUpdatedContext from '../../contexts/FormUpdatedContext';
import SaveWindowDataContext from '../../contexts/SaveWindowDataContext';
import ResetWindowDataContext from '../../contexts/ResetWindowDataContext';

/**
 * Represents a modal dialog with options to save or discard changes.
 * @typedef {Object} SaveAllDialogProps
 * @property {boolean} open - Whether the dialog is open or not.
 * @property {() => void} handleClose - A function to close the dialog.
 */

const CustomDialog = styled(Dialog)(({ theme }) => ({
  '.MuiDialog-paper': {
    position: 'absolute',
    bottom: 0,
  },
}));

/**
 * A modal dialog component that offers save or discard options for multiple windows.
 * @param {SaveAllDialogProps} props - The properties passed to the SaveAllDialog component.
 * @return {JSX.Element} The SaveAllDialog component.
 */
const SaveAllDialog = ({ open, handleClose }) => {
  /**
   * @type {FormUpdatedContextType}
   */
  const { resetFormCode } = useContext(FormUpdatedContext);

  /**
   * @type {SaveWindowDataContextType}
   */
  const { triggerAllSaves } = useContext(SaveWindowDataContext);

  /**
   * @type {ResetWindowDataContextType}
   */
  const { triggerAllResets } = useContext(ResetWindowDataContext);

  /**
   * Saves all changes on windows that were updated
   * @return {void}
   */
  const saveAllWindows = () => {
    triggerAllSaves();
    resetFormCode();
  };

  /**
   * Resets all changes on windows that were updated
   * @return {void}
   */
  const resetAllWindows = () => {
    triggerAllResets();
    resetFormCode();
  };

  return (
    <CustomDialog
      onClick={(event) => event.preventDefault()}
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      hideBackdrop={true} // Hide the backdrop to prevent click outside close
      disableEscapeKeyDown={true}
    >
      <DialogActions
        sx={{
          justifyContent: 'end',
          border: '1px solid #CFD4DB',
          boxShadow: '0px 2px 3px rgba(86, 101, 126, 0.3)',
          borderRadius: '8px',
        }}
      >
        <Button variant="outlined" color="error" onClick={resetAllWindows}>
          Discard All
        </Button>
        <Button variant="contained" onClick={saveAllWindows}>
          Save All
        </Button>
      </DialogActions>
    </CustomDialog>
  );
};

export default SaveAllDialog;
